import { Injectable } from '@angular/core';
import { FuseNavigationItem, FuseNavigationService, FuseVerticalNavigationComponent } from '@fuse/components/navigation';
import { BadisOauthLoginService } from './auth/badis-oauth-login.service';
import { CustomEventService } from './custom-event.service';
import { DataService } from './data.service';
import { GlobalVariablesService } from './global-variables.service';

import { tap, finalize } from 'rxjs/operators';

import { Observable, forkJoin } from 'rxjs';
import { CustomReportService } from '../../shared/components/custom-report-editor/custom-report.service';
import { ServiceName, ServiceReadyStateService } from './service-ready-state.service';
import { CustomReportEditor } from '@app/shared/components/custom-report-editor/models/custom-report-editor.model';
import { StyleManagerService } from './style-manager-service.service';
import { Guid } from '../constants/constants';
import { UtilityService } from '@app/modules/live/services/utility.service';
import { FuseLoadingService } from '@fuse/services/loading';
import { ExternalApplicationService } from '../../pages/external-application/services/external-application.service';
import { ExternalApplicationDTO } from '../../pages/external-application/external-application-view/models/external-application.model';

import { TranslateService } from '@ngx-translate/core';

@Injectable({
    providedIn: 'root'
})
export class SystemMenuService {
    private navComponent;
    private dynamicUserMenuClaims: any;
    private newNavigation: FuseNavigationItem[];
    public customReports: CustomReportEditor.CustomReport[] = [];
    public externalApplications: ExternalApplicationDTO.ExternalApplication[] = [];

    constructor(
        private _fuseNavigationService: FuseNavigationService,
        private _badisOauthLoginService: BadisOauthLoginService,
        private customEventService: CustomEventService,
        private varService: GlobalVariablesService,
        private customReportService: CustomReportService,
        private readyStateService: ServiceReadyStateService,
        private styleManager: StyleManagerService,
        private utility: UtilityService,
        private _dataService: DataService,
        private loadingService: FuseLoadingService,
        private externalApplicationService: ExternalApplicationService,
        private translateService: TranslateService
    ) {
        this.customEventService.on('object-types-updated').subscribe(() => this.reloadSystemMenu());
        // Re-translate navigation when language changes
        this.translateService.onLangChange.subscribe(() => {
            if (this.newNavigation && this.newNavigation.length) {
                this.translateNavigationItems(this.newNavigation);
                this.refreshMenu();
            }
        });
    }

    /**
     * Çeviri anahtarını çevirir
     */
    private translate(key: string): string {
        return this.translateService.instant(key);
    }

    public populateSystemMenu(): void {
        const newSystemMenu: any = this.varService.navizardSystemMenu;

        if (newSystemMenu === null || newSystemMenu === undefined) { return; }

        this.navComponent = this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>('mainNavigation');

        if (!this.navComponent) {
            return;
        }

        this.newNavigation = [...(newSystemMenu as FuseNavigationItem[])];
        // Titles may contain translation keys; translate them now to avoid relying on template pipes
        this.translateNavigationItems(this.newNavigation);

        // Update the navigation using the correct method
        if (this.navComponent) {
            this.navComponent.navigation = this.newNavigation;
        }

        this.dynamicUserMenuClaims = this._badisOauthLoginService.userAccessToken.dynamicUserMenuClaims;

        this.reloadSystemMenu();

        this.newNavigation.forEach((menuItem: any) => {
            menuItem.hidden = menuItem.hide || (menuItem.roles && menuItem.roles.length > 0 &&
                !this._badisOauthLoginService.isUserInRoles(menuItem.roles || []) &&
                !this.userHasNoDynaminMenuClaims(menuItem)
            );

            // also apply for child items
            if (menuItem.children) {
                menuItem.children.forEach((childItem: any) => {
                    const isInRoles = childItem.roles && childItem.roles.length > 0 ?
                        !this._badisOauthLoginService.isUserInRoles(childItem.roles) :
                        true; // roles yoksa erişime izin ver
                    const isInClaims = !this.userHasNoDynaminMenuClaims(childItem.id);
                    childItem.hidden = childItem.hide || (childItem.roles && childItem.roles.length > 0 &&
                        isInRoles &&
                        isInClaims
                    );
                });
            }
        });
    }
    private translateNavigationItems(items: FuseNavigationItem[] | undefined): void {
        if (!items) { return; }
        for (const item of items) {
            if (!item) { continue; }
            const anyItem: any = item as any;

            // Title
            if (typeof item.title === 'string') {
                // Preserve original key once to support re-translation on language change
                if (!anyItem.meta) { anyItem.meta = {}; }
                if (!anyItem.meta.i18nTitleKey && this.isLikelyI18nKey(item.title)) {
                    anyItem.meta.i18nTitleKey = item.title;
                }
                const key = anyItem.meta?.i18nTitleKey;
                if (typeof key === 'string') {
                    item.title = this.translate(key) || item.title;
                }
            }

            // Subtitle
            if (typeof anyItem.subtitle === 'string') {
                if (!anyItem.meta) { anyItem.meta = {}; }
                if (!anyItem.meta.i18nSubtitleKey && this.isLikelyI18nKey(anyItem.subtitle)) {
                    anyItem.meta.i18nSubtitleKey = anyItem.subtitle;
                }
                const subKey = anyItem.meta?.i18nSubtitleKey;
                if (typeof subKey === 'string') {
                    anyItem.subtitle = this.translate(subKey) || anyItem.subtitle;
                }
            }

            if (item.children && item.children.length) {
                this.translateNavigationItems(item.children);
            }
        }
    }

    private isLikelyI18nKey(value: string): boolean {
        // Heuristic: keys commonly contain dots and no spaces
        return !!value && value.includes('.') && !/\s/.test(value);
    }
    /**
     * Lazily resolve and cache the main vertical navigation component.
     */
    private getNavComponent(): FuseVerticalNavigationComponent | null {
        if (!this.navComponent) {
            this.navComponent = this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>('mainNavigation');
        }
        return this.navComponent ?? null;
    }
    protected async reloadSystemMenu(): Promise<void> {
        const nav = this.getNavComponent();
        if (!nav || !nav.navigation) {
            // Navigation component not ready yet; skip for now.
            return;
        }
        const customObjectTypeMenu: FuseNavigationItem = this._fuseNavigationService.getItem('definitions-tracking-item-types', nav.navigation);

        if (customObjectTypeMenu) {
            this.getCustomObjectTypeMenu().subscribe((response): void => {
                customObjectTypeMenu.children = response.map((menu) => {
                    let iconWithNameSpace = 'fal:times-hexagon';

                    try {
                        if (menu?.icon) {
                            const iconConfig = `${menu.icon}`.replace(/'/g, '"');

                            if (iconConfig) {
                                const icon = JSON.parse(iconConfig);

                                if (icon) { iconWithNameSpace = `${icon[0]}:${icon[1]}`; }
                            }
                        }
                    }
                    catch { }

                    return {
                        id: menu.id,
                        title: menu.name,
                        link: `object-item-tracking/${menu.id}`,
                        icon: iconWithNameSpace,
                        hidden: !this.userHasNoDynaminMenuClaims(menu.id),
                        badge: {
                            title: menu.objectsCount,
                            classes: 'px-2 bg-gray-600 text-white rounded-full'
                        },
                        type: 'basic'
                    };
                });
            });
        }

        (await this.loadAllMenus()).subscribe(() => {
            this.readyStateService.setServiceReady(ServiceName.Menu, true);
        });
    }

    private async loadAllMenus(): Promise<Observable<any>> {
        return forkJoin([
            this.getUserCustomReports().pipe(
                tap(reports => {
                    this.customReports = reports;
                    // Apply the freshly fetched reports directly to the navigation
                    this.applyCustomReportsMenu(reports);
                })
            ),
            await this.refreshExternalApplications()
        ]);
    }

    private getCustomObjectTypeMenu(): any {
        return this._dataService.getAny<any>('track-item-type/active');
    }

    private userHasNoDynaminMenuClaims(id: string): boolean {
        return this.dynamicUserMenuClaims?.includes(id);
    }

    public getCustomReportById(id: string): CustomReportEditor.CustomReport {
        return this.customReports.find((report) => report.id.toString() === id);
    }

    private getUserCustomReports(): Observable<CustomReportEditor.CustomReport[]> {
        return this.customReportService.getUserCustomReports();
    }

    // Custom reports menu refresh function (fetch + apply)
    public refreshCustomReportsMenu(): Observable<CustomReportEditor.CustomReport[]> {
        this.loadingService.setAutoMode(false);

        return this.getUserCustomReports().pipe(
            tap((reports: CustomReportEditor.CustomReport[]) => {
                this.customReports = reports; // keep cache
                this.applyCustomReportsMenu(reports);
            }),
            finalize(() => {
                this.loadingService.setAutoMode(true);
            })
        );
    }

    /**
     * Apply provided custom reports to navigation under the Reports group.
     * Pure side-effect, no fetching.
     */
    private applyCustomReportsMenu(reports: CustomReportEditor.CustomReport[]): void {
        const nav = this.getNavComponent();
        if (!nav || !nav.navigation) {
            // Nav component isn't ready; skip UI update for now.
            return;
        }

        // Try to resolve the Reports group robustly
        const reportsMenu = this.findReportsGroup(nav.navigation);
        if (!reportsMenu) {
            return;
        }

        // Remove previous custom-reports section if exists
        reportsMenu.children = reportsMenu.children?.filter(child => child.id !== 'custom-reports') || [];

        // Build the Custom Reports section
        const customReportsMenu: FuseNavigationItem = {
            id: 'custom-reports',
            title: this.translate('SystemAdmin.Menu.Reports.CustomReports.Title'),
            icon: 'fal:file-chart-column',
            type: 'collapsable',
            classes: { wrapper: 'text-yellow-500' },
            badge: {
                title: 'Beta',
                classes: "inline-flex items-center rounded-md bg-red-400/10 px-2 py-1 text-xs font-medium text-red-500 ring-1 ring-inset ring-red-400/20"
            },
            meta: { i18nTitleKey: 'SystemAdmin.Menu.Reports.CustomReports.Title' },
            children: (reports?.length ?? 0) > 0
                ? [...reports]
                    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
                    .map((report) => this.generateCustomReportMenuItem(report))
                : []
        };

        if (!reportsMenu.children) {
            reportsMenu.children = [];
        }
        reportsMenu.children.push(customReportsMenu);
        nav.refresh();
    }

    /**
     * Attempt to locate the Reports group in the navigation with fallbacks.
     */
    private findReportsGroup(items: FuseNavigationItem[]): FuseNavigationItem | undefined {
        // First try the canonical id
        const direct = this._fuseNavigationService.getItem('reports', items);
        if (direct) return direct;

        // Fallback: search recursively by common hints
        const stack: FuseNavigationItem[] = [...items];
        while (stack.length) {
            const item = stack.shift();
            if (!item) { continue; }
            const anyItem: any = item as any;
            const idStr = (anyItem.id ?? '').toString().toLowerCase();
            const key = anyItem.meta?.i18nTitleKey as string | undefined;
            // Heuristics: id contains 'report' or i18n key contains '.Reports.'
            if ((idStr && idStr.includes('report')) || (typeof key === 'string' && key.includes('.Reports.'))) {
                return item;
            }
            if (item.children && item.children.length) {
                stack.push(...item.children);
            }
        }
        return undefined;
    }

    public getMenuItemById(id: string | Guid): FuseNavigationItem | undefined {
        if (!this.navComponent || !this.navComponent.navigation) {
            console.error('Navigation component or navigation data is not initialized.');
            return undefined;
        }
        return this.findMenuItemById(this.navComponent.navigation, id);
    }

    private findMenuItemById(items: FuseNavigationItem[], id: string | Guid): FuseNavigationItem | undefined {
        for (const item of items) {
            if (item.id === id) {
                return item;
            }
            if (item.children) {
                const found = this.findMenuItemById(item.children, id);
                if (found) {
                    return found;
                }
            }
        }
        return undefined;
    }

    public refreshMenu(): void {
        const nav = this.getNavComponent();
        if (!nav) { return; }
        nav.refresh();
    }

    sortCustomMenuItems(menuId: string) {
        const customReportsMenu = this.getMenuItemById(menuId);
        if (!customReportsMenu) {
            console.error('Custom reports menu not found.');
            return;
        }

        customReportsMenu.children = customReportsMenu.children.sort((a, b) => {
            const aOrder = a.meta?.order ?? 0;
            const bOrder = b.meta?.order ?? 0;
            return aOrder - bOrder;
        });
    }

    generateCustomReportMenuItem(report: CustomReportEditor.CustomReport): FuseNavigationItem {
        let link: string;
        switch (report.groupKey) {
            case 'Sensor':
                link = `/live-tag-properties/${report.id}`;
                break;
            case 'Location':
                link = `/live-tag-locations/${report.id}`;
                break;
            case 'SensorHistory':
                link = `/reports/tag-property/${report.id}`;
                break;
            case 'LocationHistory':
                link = `/reports/tracking/${report.id}`;
                break;
            case 'ZoneEngagement':
                link = `/reports/zone-engagement/${report.id}`;
                break;
            default:
                console.error('Unknown report groupKey:', report.groupKey);
                break;
        }

        let titleColor = "text-white";
        if (report?.colorCode)
            titleColor = this.styleManager.addColorClass(report?.colorCode)

        let wrapperColor = "text-white";
        if (report?.iconColorCode)
            wrapperColor = this.styleManager.addColorClass(report?.iconColorCode);

        const icon = this.utility.flatIcon(report.icon) ?? 'fal:file-chart-column';

        const badge = report?.isPublic ? null : {
            title: '●',
            "classes": "inline-flex items-center  px-2 py-1 text-xs font-medium text-yellow-500 "
        };

        return {
            id: report.id.toString(),
            title: report.name,
            icon: icon,
            badge: badge,
            link: link,
            exactMatch: true,
            type: 'basic',
            classes: { wrapper: wrapperColor, title: titleColor },
            meta: { order: report.order }
        };
    }

    getCustomReportMenuItem(id: Guid): CustomReportEditor.CustomReport {
        return this.customReports.find(report => report.id === id);
    }

    addCustomReportMenuItem(reportModel: CustomReportEditor.CustomReport) {
        // Önce customReports array'ine ekle
        this.customReports.push(reportModel);
        // Menüyü yeniden uygula (yeniden fetch etmeye gerek yok)
        this.applyCustomReportsMenu(this.customReports);
    }

    updateCustomReportMenuItem(report: CustomReportEditor.CustomReport) {
        const menuItem = this.getMenuItemById(report.id);
        if (menuItem) {
            const needSort = menuItem.meta.order != report.order;

            const updatedItem = this.generateCustomReportMenuItem(report);
            Object.assign(menuItem, updatedItem);

            if (needSort) {
                this.sortCustomMenuItems("custom-reports");
            }

            this.navComponent.refresh();
        }
    }

    removeCustomReportMenuItem(id: Guid) {
        const menuItem = this.getMenuItemById(id);
        if (menuItem) {
            const parent = this.getMenuItemById("custom-reports");
            if (parent) {
                parent.children = parent.children.filter(child => child.id !== id);
                this.navComponent.refresh();
            }
        }
    }

    public updateExternalAppsMenu(): void {
        if (!this.navComponent) return;

        const separator: FuseNavigationItem = {
            id: 'external-apps-separator',
            type: 'divider'
        };


        const externalApps: FuseNavigationItem = {
            id: 'external-apps',
            title: 'Harici Uygulamalar',
            subtitle: 'Dış sistemler ve uygulamalar',
            type: 'group',
            icon: 'fal:globe',
            children: [
                {
                    id: 'new-external-app',
                    title: 'Yeni Uygulama Ekle',
                    type: 'basic' as const,
                    icon: this.utility.flatIcon(['fal', 'plus-circle']),
                    link: '/apps/new',
                    exactMatch: true,
                    badge: {
                        title: 'Beta',
                        classes: "inline-flex items-center rounded-md bg-red-400/10 px-2 py-1 text-xs font-medium text-red-500 ring-1 ring-inset ring-red-400/20"
                    }
                },
                ...this.externalApplications
                    .filter(app => app.isPublic)
                    .sort((a, b) => (a.order || 0) - (b.order || 0))
                    .map(app => ({
                        id: app.id.toString(),
                        title: app.menuTitle || app.title,
                        type: 'basic' as const,
                        icon: this.utility.flatIcon(app.menuIcon || app.icon) || 'fal:globe',
                        link: `/apps/${app.id}`,
                        exactMatch: true,
                        classes: {
                            title: this.styleManager.addColorClass(app.menuTitleColor),
                            wrapper: this.styleManager.addColorClass(app.menuIconColor)
                        }
                    }))
            ]
        };

        // Find and update external apps in navigation
        const navigation = this.navComponent.navigation;
        const externalAppsIndex = navigation.findIndex(item => item.id === 'external-apps');
        const definitionsIndex = navigation.findIndex(item => item.id === 'definitions');

        if (externalAppsIndex >= 0) {
            // If external apps already exist, update in place
            navigation[externalAppsIndex] = externalApps;

            // If it needs to be moved before definitions
            if (definitionsIndex >= 0 && externalAppsIndex > definitionsIndex) {
                // Remove from current position and add before definitions
                navigation.splice(externalAppsIndex, 1);
                navigation.splice(definitionsIndex, 0, externalApps);
            }
        } else {
            // Add new external apps section before definitions if definitions exists
            if (definitionsIndex >= 0) {
                navigation.splice(definitionsIndex, 0, separator);
                navigation.splice(definitionsIndex + 1, 0, externalApps);
            } else {
                // Fallback to old behavior if definitions not found
                navigation.push(separator);
                navigation.push(externalApps);
            }
        }

        this.navComponent.refresh();
    }

    public addExternalApplication(application: ExternalApplicationDTO.ExternalApplication): void {
        this.externalApplications.push(application);
        this.updateExternalAppsMenu();
    }

    public updateExternalApplication(application: ExternalApplicationDTO.ExternalApplication): void {
        const index = this.externalApplications.findIndex(app => app.id === application.id);
        if (index !== -1) {
            this.externalApplications[index] = application;
            this.updateExternalAppsMenu();
        }
    }

    public removeExternalApplication(id: string): void {
        this.externalApplications = this.externalApplications.filter(app => app.id !== id);
        this.updateExternalAppsMenu();
    }

    public getExternalApplicationById(id: string): ExternalApplicationDTO.ExternalApplication {
        return this.externalApplications.find(app => app.id === id);
    }

    public refreshExternalApplications(): Observable<ExternalApplicationDTO.ExternalApplication[]> {
        return this.externalApplicationService.getAll().pipe(
            tap(applications => {
                this.externalApplications = applications;
                this.updateExternalAppsMenu();
            })
        );
    }

    /**
     * Navigation'dan raporları ham şekilde getirir
     * @returns Ham report öğelerini döner (UI formatı olmadan)
     */
    public getNavigationReports(): any {

        // Navigation'dan raporları çek
        const navComponent = this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>('mainNavigation');
        if (navComponent) {
            const reportsMenu = this._fuseNavigationService.getItem('reports', navComponent.navigation);

            return reportsMenu;

        }

        return null;
    }

    /**
     * Harici uygulamaların ham listesini döner
     * @returns Harici uygulamaların ham listesi
     */
    public getRawExternalApps(): ExternalApplicationDTO.ExternalApplication[] {
        return this.externalApplications ? [...this.externalApplications].sort((a, b) => (a.order || 0) - (b.order || 0)) : [];
    }
}
